import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';

/**
 * Creates a JSON.stringify replacer that safely handles circular references
 * and serializes Error objects.
 * Repeated object references are returned as '[Circular]' to prevent serialization
 * errors, and Error instances are transformed into objects with name, message,
 * and stack for better visibility in the modal's Show Details section.
 * @returns {Function} JSON.stringify replacer function with signature (key, value).
 */
const getCircularReplacer = () => {
  const seen = new WeakSet();

  const splitNewlines = (str) => str.split('\n').map((line) => line.trim());

  return (key, value) => {
    if (typeof value === 'string' && value.includes('\n')) {
      return splitNewlines(value);
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack ? splitNewlines(value.stack) : [],
      };
    }

    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }

      seen.add(value);
    }

    return value;
  };
};

/**
 * Generic blocking error modal with a static backdrop.
 * When `details` is provided, a toggleable
 * "Show Details" section is rendered below the body text to display technical
 * error information as formatted JSON.
 * @param {object} props Component props.
 * @param {Function} props.onCancelFunc Cancel handler.
 * @param {Function} [props.onRetryFunc] Retry handler.
 * @param {boolean} [props.retryDisabled] Disables Retry when retry cannot succeed.
 * @param {object|Array} [props.details] Generic diagnostic data displayed as
 *   formatted JSON in a toggleable "Show Details" section. Omit to hide the section.
 * @param {string} [props.bodyText] Optional custom body message. When provided,
 *   overrides the default i18n body message.
 * @returns {React.ReactElement} Modal element.
 */
function ErrorModal({
  onCancelFunc, onRetryFunc = null, retryDisabled = false, details = null, bodyText = null,
}) {
  const intl = useIntl();
  const [showDetails, setShowDetails] = useState(false);

  const resolvedHeaderText = intl.formatMessage({
    id: 'enosikit.error_modal.header.title',
    defaultMessage: 'Data fetch error',
  });
  const resolvedBodyText = bodyText ?? intl.formatMessage({
    id: 'enosikit.error_modal.body.message',
    defaultMessage: 'An error occurred while loading data. Please try again.',
  });
  const cancelLabel = intl.formatMessage({
    id: 'enosikit.error_modal.close.button.label',
    defaultMessage: 'Close',
  });
  const retryLabel = intl.formatMessage({
    id: 'enosikit.error_modal.retry.button.label',
    defaultMessage: 'Retry',
  });
  const showDetailsLabel = intl.formatMessage({
    id: 'enosikit.error_modal.show_details.button.label',
    defaultMessage: 'Show Details',
  });
  const hideDetailsLabel = intl.formatMessage({
    id: 'enosikit.error_modal.hide_details.button.label',
    defaultMessage: 'Hide Details',
  });

  return (
    <Modal
      isOpen
      backdrop="static"
      keyboard
      toggle={onCancelFunc}
      centered
      zIndex={10000}
    >
      <ModalHeader toggle={onCancelFunc}>
        {resolvedHeaderText}
      </ModalHeader>
      <ModalBody>
        <p className="mb-3">{resolvedBodyText}</p>
        {details != null && (
          <div className="mb-3">
            <Button
              size="sm"
              type="button"
              className="btn btn-darken"
              onClick={() => setShowDetails((prev) => !prev)}
            >
              {showDetails ? hideDetailsLabel : showDetailsLabel}
            </Button>
            {showDetails && (
              <pre
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  background: '#f8f9fa',
                  fontSize: '0.8rem',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  marginBottom: 0,
                }}
              >
                {JSON.stringify(details, getCircularReplacer(), 2)}
              </pre>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" type="button" onClick={onCancelFunc} className="me-2">
          {cancelLabel}
        </Button>
        {onRetryFunc && (
        <Button
          color="primary"
          type="button"
          onClick={onRetryFunc}
          disabled={retryDisabled}
        >
          {retryLabel}
        </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

ErrorModal.propTypes = {
  onCancelFunc: PropTypes.func.isRequired,
  onRetryFunc: PropTypes.func,
  retryDisabled: PropTypes.bool,
  details: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  bodyText: PropTypes.string,
};

export default ErrorModal;
